import { StateCreator } from 'zustand';
import { useInstitutionsStore } from '../institutions';
import { IUsersSlice, TUsersStore } from './types';
import { usersApi } from '@/API';
import {
  EUserRoles,
  TChangeUserBillingInstitutionRequest,
  TUpdateUserByRoleAndInstitutionRequest,
  type TUserDataCamel,
} from '@/types';
import { convertKeys } from '@/utils/common';
import { sortByUserInstitutionRoles } from '@/utils/users';

export const usersSlice: StateCreator<TUsersStore, [], [], IUsersSlice> = (
  set,
  get,
) => ({
  setUsers: (users) => set({ users }),
  // this arary is for casual user
  users: [],
  // this array is for admin
  allUsers: [],
  loading: false,

  filterUsersByRoles: (roles) =>
    get().users.filter(({ userInstitutionRoles }) =>
      userInstitutionRoles.some(({ role }) => roles.includes(role)),
    ),

  getUserById: (id) => {
    return get().users.find((u) => u.id === id) ?? null;
  },

  fetchUserById: async (id: string) => {
    try {
      const response = await usersApi.getUserById(id);
      const user = convertKeys(response.data.data);
      get().updateUser(user);
      return user;
    } catch (error) {
      console.error('Failed to fetch user by id:', error);
      throw error;
    }
  },

  getMyPatientsByAppointments: (appointments) => {
    const currentInstitutionId =
      useInstitutionsStore.getState().currentInstitution?.id;
    const uniqueUserIds = new Set(
      appointments.flatMap((a) =>
        a.participants.map((p) => p.userInstitutionRoleId),
      ),
    );
    return get()
      .filterUsersByRoles([EUserRoles.DEVICE, EUserRoles.PATIENT])
      .filter((user) =>
        user.userInstitutionRoles.find(
          ({ institutionId, id }) =>
            institutionId === currentInstitutionId && uniqueUserIds.has(id),
        ),
      );
  },

  fetchUsersForAdmin: async (institutionId: string) => {
    const isSuperAdmin = get().checkIsSuperAdmin();
    const { userData, getOrgAdminOrganizationIds } = get();
    const orgAdminOrganizationIds = getOrgAdminOrganizationIds();
    const isOrgAdmin = !isSuperAdmin && !!orgAdminOrganizationIds.length;

    // Filter institutions to only those where user has ADMIN role.
    // This is needed because users can have different roles in different institutions
    // (e.g., ADMIN in institution 1, COORDINATOR in institution 2).
    // We should only fetch users from institutions where user has ADMIN permissions.
    const adminInstitutionIds =
      userData?.userInstitutionRoles
        .filter(({ role }) => role === EUserRoles.ADMIN)
        .map(({ institutionId }) => institutionId) ?? [];

    const institutionsToFetch = isOrgAdmin
      ? useInstitutionsStore
          .getState()
          .institutions.filter((institution) =>
            orgAdminOrganizationIds.includes(institution.organizationId),
          )
          .map((institution) => institution.id)
      : !isSuperAdmin && userData
        ? adminInstitutionIds.length > 0
          ? adminInstitutionIds
          : [institutionId]
        : undefined;

    if (isOrgAdmin && !institutionsToFetch?.length) {
      set({ allUsers: [] });
      return;
    }

    const res = await usersApi.getUsers(
      isSuperAdmin ? undefined : { institution: institutionsToFetch },
    );
    // Filter users by institution ID to get only users
    // from current institution
    const result = res.data.data.filter(({ user_institution_roles }) =>
      user_institution_roles.some(
        ({ institution_id }) => institution_id === institutionId,
      ),
    );
    set({ allUsers: [...convertKeys(result)] });
  },

  getUsers: async (institutionId: string) => {
    set({ loading: true });
    try {
      const {
        userData,
        role,
        allRoles,
        checkIsSuperAdmin,
        getOrgAdminOrganizationIds,
        fetchUsersForAdmin,
      } = get();
      if (!userData) return;

      const hasAdminRole = allRoles.includes(EUserRoles.ADMIN);
      const isSuperAdmin = checkIsSuperAdmin();
      const isOrgAdmin = !!getOrgAdminOrganizationIds().length;

      // If user has admin role, is an org admin, or is super admin,
      // fetch all users without role filtering
      if (hasAdminRole || isSuperAdmin || isOrgAdmin) {
        await fetchUsersForAdmin(institutionId);
      }

      if (!role) return;

      const isPatient = [EUserRoles.PATIENT, EUserRoles.DEVICE].includes(role);
      // Role-based configuration for fetching users based on current user's role
      const roleConfig: Record<EUserRoles, EUserRoles[] | undefined> = {
        [EUserRoles.PROVIDER]: [
          EUserRoles.PATIENT,
          EUserRoles.PROVIDER,
          EUserRoles.COORDINATOR,
          EUserRoles.DEVICE,
          EUserRoles.GUEST,
        ],
        [EUserRoles.COORDINATOR]: [
          EUserRoles.PATIENT,
          EUserRoles.PROVIDER,
          EUserRoles.COORDINATOR,
          EUserRoles.DEVICE,
          EUserRoles.GUEST,
        ],
        [EUserRoles.GUEST]: [
          EUserRoles.PATIENT,
          EUserRoles.GUEST,
          EUserRoles.COORDINATOR,
          EUserRoles.PROVIDER,
        ],
        [EUserRoles.PATIENT]: [
          EUserRoles.PROVIDER,
          EUserRoles.COORDINATOR,
          EUserRoles.GUEST,
        ],
        [EUserRoles.DEVICE]: [
          EUserRoles.PROVIDER,
          EUserRoles.COORDINATOR,
          EUserRoles.GUEST,
        ],
        [EUserRoles.PRESENTER]: [
          EUserRoles.PATIENT,
          EUserRoles.PROVIDER,
          EUserRoles.COORDINATOR,
          EUserRoles.GUEST,
          EUserRoles.PRESENTER,
        ],
        [EUserRoles.ADMIN]: undefined,
      };

      const rolesToFetch = roleConfig[role];

      const users: TUserDataCamel[] = isPatient ? [userData] : [];
      const response = await usersApi.getUsers({
        institution: [institutionId],
        role: rolesToFetch,
      });
      users.push(...convertKeys(response.data.data));

      set({ users });
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      set({ loading: false });
    }
  },

  updateUser: (updatedUser, updateAllUsersArray = true) => {
    set((state) => {
      const users = state.users.some((user) => user.id === updatedUser.id)
        ? state.users.map((user) =>
            user.id === updatedUser.id ? { ...user, ...updatedUser } : user,
          )
        : [...state.users, updatedUser];
      if (updateAllUsersArray) {
        const allUsers = state.allUsers.some(
          (user) => user.id === updatedUser.id,
        )
          ? state.allUsers.map((user) =>
              user.id === updatedUser.id ? { ...user, ...updatedUser } : user,
            )
          : [...state.allUsers, updatedUser];
        return { users, allUsers };
      }
      return { users };
    });
  },

  getUserInstitutionRoleIdById: (
    id,
    role = [EUserRoles.PROVIDER, EUserRoles.COORDINATOR],
  ) => {
    const usersForAdmin = get().allUsers;
    const usersForOtherRoles = get().users;

    const users = get().checkRole(EUserRoles.ADMIN)
      ? usersForAdmin
      : usersForOtherRoles;
    const currentInstitutionId =
      useInstitutionsStore.getState().currentInstitution?.id;
    const user = users.find((user) => user.id === id);
    if (!user) return undefined;
    const sortedRoles = sortByUserInstitutionRoles(user.userInstitutionRoles);
    const roles = Array.isArray(role) ? role : [role];
    return sortedRoles.find(
      ({ role, institutionId }) =>
        roles.includes(role) && institutionId === currentInstitutionId,
    )?.id;
  },

  getUserByInstitutionRoleId: (userInstitutionRoleId: string) => {
    const usersForAdmin = get().allUsers;
    const usersForOtherRoles = get().users;

    const users = get().checkRole(EUserRoles.ADMIN)
      ? usersForAdmin
      : usersForOtherRoles;
    const user = users.find(({ userInstitutionRoles }) =>
      userInstitutionRoles.some(({ id }) => id === userInstitutionRoleId),
    );

    if (!user) return undefined;

    const userInstitutionRole = user.userInstitutionRoles.find(
      ({ id }) => id === userInstitutionRoleId,
    );

    if (!userInstitutionRole) return undefined;

    return {
      ...user,
      userInstitutionRole,
    };
  },

  getAllRolesByUserId: (userId: string) => {
    const user = get().allUsers.find((u) => u.id === userId);
    const userRoles = user?.userInstitutionRoles.map((v) => v.role);
    return Array.from(new Set(userRoles));
  },

  getInstitutionIdsByUserId: (userId: string) => {
    const user = get().allUsers.find((u) => u.id === userId);
    const institutionIds = user?.userInstitutionRoles.map(
      ({ institutionId }) => institutionId,
    );
    return Array.from(new Set(institutionIds));
  },

  getAvailabilityByUserId: (userId: string) => {
    const user = get().allUsers.find((u) => u.id === userId);
    return !!user?.availability?.isAvailable;
  },

  /**
   * Updates a user's role and/or institution assignment.
   * @param body - The user id and the role/institution to assign (camelCase).
   * @throws A localized `admin-panel.please-try-later` error when the API call fails.
   */
  updateUserByInstitutionRoles: async (
    body: TUpdateUserByRoleAndInstitutionRequest,
  ) => {
    try {
      await usersApi.updateUserByInstitutionRoles(convertKeys(body, 'snake'));
    } catch (error) {
      console.error('Failed to update user institution roles', error);
      throw error;
    }
  },

  /**
   * Changes which institution a user's billing is currently attributed to.
   * @param body - The user id and the institution id to bill going forward (camelCase).
   * @throws Rethrows and logs the original error when the API call fails.
   */
  changeUserBillingInstitution: async (
    body: TChangeUserBillingInstitutionRequest,
  ) => {
    try {
      await usersApi.changeUserBillingInstitution(convertKeys(body, 'snake'));
    } catch (error) {
      console.error('Failed to change user billing institution', error);
      throw error;
    }
  },

  updateUserStatus: async (userId: string, isActive: boolean) => {
    set({ loading: true });
    try {
      await usersApi.setActiveStatus(userId, isActive);
      const user = get().getUserById(userId);
      // don't update allUsers array here because
      // users array has less information about userInstitutionRoles
      // and updating allUsers array with users data leads to data loss
      // allUsers is updated separatly below
      if (user) get().updateUser({ ...user, isActive }, false);
      set({
        allUsers: get().allUsers.map((user) =>
          user.id === userId ? { ...user, isActive } : user,
        ),
      });
    } catch (error) {
      console.error('Failed to update user status', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  /**
   * Activates a user pending activation, optionally with a new email, and syncs the
   * activated user into `users` and `allUsers`. Activation sends the user an invitation,
   * so the current institution's invites are refetched before `loading` is cleared, keeping
   * the "Activate" button from being clicked again before the new invite is in `invites`;
   * a failed refetch doesn't fail the activation.
   * @param userId - The id of the user to activate.
   * @param email - The email to set for the user; omitted to keep the current one.
   * @returns The activated user returned by the API.
   * @throws Rethrows and logs the original error when the API call fails.
   */
  activateUser: async (userId: string, email?: string) => {
    set({ loading: true });
    try {
      const response = await usersApi.activateUser(userId, email);
      const updatedUser = convertKeys(response.data.data);
      const user = get().getUserById(userId);
      // don't update allUsers array via updateUser for the same reason as in updateUserStatus
      if (user) get().updateUser({ ...user, ...updatedUser }, false);
      set({
        allUsers: get().allUsers.map((user) =>
          user.id === userId ? { ...user, ...updatedUser } : user,
        ),
      });
      const institutionId =
        useInstitutionsStore.getState().currentInstitution?.id;
      if (institutionId)
        await get()
          .fetchInvites(institutionId)
          .catch(() => {});
      return updatedUser;
    } catch (error) {
      console.error('Failed to activate user', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  removeUserRole: async (roleId: string) => {
    try {
      await usersApi.removeUserRole(roleId);
      const userWithRole = get().allUsers.find(({ userInstitutionRoles }) =>
        userInstitutionRoles.some(({ id }) => id === roleId),
      );
      if (userWithRole) {
        const currentInstitutionId =
          useInstitutionsStore.getState().currentInstitution?.id;
        const remainingRoles = userWithRole.userInstitutionRoles.filter(
          ({ id }) => id !== roleId,
        );
        const updatedUser = {
          ...userWithRole,
          userInstitutionRoles: remainingRoles,
        };
        const hasRoleInCurrentInstitution = remainingRoles.some(
          ({ institutionId }) => institutionId === currentInstitutionId,
        );
        const updatedUsers = hasRoleInCurrentInstitution
          ? get().users.map((user) =>
              user.id === userWithRole.id ? updatedUser : user,
            )
          : get().users.filter((user) => user.id !== userWithRole.id);
        const updatedAllUsers = hasRoleInCurrentInstitution
          ? get().allUsers.map((user) =>
              user.id === userWithRole.id ? updatedUser : user,
            )
          : get().allUsers.filter((user) => user.id !== userWithRole.id);

        set({
          users: updatedUsers,
          allUsers: updatedAllUsers,
        });
      }
    } catch (error) {
      console.error('Failed to remove user role', error);
      throw error;
    }
  },
});
